'use client';
import React, { useState } from 'react';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { CheckCircle, XCircle, Ban } from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_ENTRIES = [
  { id: 'fg-001', dogName: 'Biscuit', author: 'anonymous_user_7732', message: 'Buy followers and paws for your dog! Visit [spam link removed] for instant results!', reportedBy: 'system-ai', reportedAt: 'Jun 22, 2026', aiScore: 98, reason: 'Spam / promotional content' },
  { id: 'fg-002', dogName: 'Luna', author: 'hater_troll_99', message: 'This dog is ugly and so is the owner. Why do people post dogs online anyway?', reportedBy: 'profile-owner', reportedAt: 'Jun 21, 2026', aiScore: 74, reason: 'Harassment / abusive language' },
];

export default function FlaggedGuestbookTable() {
  const [entries, setEntries] = useState(INITIAL_ENTRIES);
  const [confirmAction, setConfirmAction] = useState<{ id: string; action: string } | null>(null);

  const handleAction = (id: string, action: string) => {
    setEntries(e => e.filter(entry => entry.id !== id));
    setConfirmAction(null);
    const labels: Record<string, string> = {
      approve: 'Guestbook entry approved and restored.',
      remove: 'Guestbook entry removed.',
      suspend: 'User suspended and entry removed.',
    };
    toast.success(labels[action] || 'Action completed.');
  };

  return (
    <>
      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-5 py-3 text-xs font-600 text-muted-foreground">Dog Profile</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Author</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Message</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Flag Reason</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Reported By</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Date</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">AI Score</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-700 text-sm text-foreground">{entry.dogName}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground font-600">{entry.author}</span>
                  </td>
                  <td className="px-4 py-4 max-w-[220px]">
                    <p className="text-sm text-foreground/80 line-clamp-2 leading-relaxed">{entry.message}</p>
                  </td>
                  <td className="px-4 py-4 max-w-[150px]">
                    <p className="text-xs text-danger font-600">{entry.reason}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground">{entry.reportedBy}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{entry.reportedAt}</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`text-sm font-800 stat-badge ${entry.aiScore >= 70 ? 'text-danger' : 'text-warning'}`}>
                      {entry.aiScore}%
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setConfirmAction({ id: entry.id, action: 'approve' })}
                        className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center text-success hover:bg-green-100 transition-colors"
                        title="Approve entry — mark as safe and keep"
                      >
                        <CheckCircle size={13} />
                      </button>
                      <button
                        onClick={() => setConfirmAction({ id: entry.id, action: 'remove' })}
                        className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center text-danger hover:bg-red-100 transition-colors"
                        title="Remove entry from guestbook permanently"
                      >
                        <XCircle size={13} />
                      </button>
                      <button
                        onClick={() => setConfirmAction({ id: entry.id, action: 'suspend' })}
                        className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center text-warning hover:bg-orange-100 transition-colors"
                        title="Suspend user and remove entry — this cannot be undone"
                      >
                        <Ban size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CheckCircle size={40} className="text-success mb-3" />
            <p className="font-700 text-base text-foreground">Guestbook queue is clear</p>
            <p className="text-sm text-muted-foreground mt-1">All guestbook reports have been reviewed.</p>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={
          confirmAction?.action === 'approve' ? 'Approve Guestbook Entry' :
          confirmAction?.action === 'remove'? 'Remove Guestbook Entry' : 'Suspend User & Remove Entry'
        }
        description={
          confirmAction?.action === 'approve' ? 'This will mark the entry as safe and dismiss the flag.' :
          confirmAction?.action === 'remove' ? 'This will permanently delete this entry from the guestbook.' :
          'This will suspend the user account and remove the entry. The user will be notified of the suspension.'
        }
        confirmLabel={
          confirmAction?.action === 'approve' ? 'Approve' :
          confirmAction?.action === 'remove'? 'Remove Entry' : 'Suspend User'
        }
        destructive={confirmAction?.action !== 'approve'}
        onConfirm={() => confirmAction && handleAction(confirmAction.id, confirmAction.action)}
        onCancel={() => setConfirmAction(null)}
      />
    </>
  );
}